import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BROKER } from "../lib/data";
import { Menu, X, Phone } from "lucide-react";

const NAV = [
  { id: "producer", label: "Meet Your Producer" },
  { id: "products", label: "Products" },
  { id: "necessity", label: "Why It Matters" },
  { id: "carriers", label: "Carriers" },
  { id: "contact", label: "Get a Quote" },
];

const NavBar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, []);

  const go = (id) => {
    setOpen(false);
    // Wait briefly for menu collapse, then jump. We use a manual smooth
    // scroll loop to avoid framer-motion's height animation interfering with
    // window.scrollTo({behavior:'smooth'}) on some mobile browsers.
    setTimeout(() => {
      const el = document.getElementById(id);
      if (!el) return;
      const headerOffset = 80;
      const target = el.getBoundingClientRect().top + window.scrollY - headerOffset;
      window.scrollTo({ top: target, behavior: "smooth" });
    }, 50);
  };

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-ivory/85 backdrop-blur-xl border-b border-line/60 shadow-sm" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <button onClick={() => go("hero")} className="flex items-center gap-3 group" data-testid="nav-logo">
          <span className="relative w-10 h-10 bg-ink flex items-center justify-center font-display text-gold text-lg tracking-wider overflow-hidden">
            <span className="relative z-10">Z</span>
            <span className="absolute -inset-1 conic-gold opacity-30" />
          </span>
          <div className="hidden sm:flex flex-col items-start leading-tight">
            <span className="font-display text-ink text-base">{BROKER.name}</span>
            <span className="eyebrow text-gold text-[10px]">Life Insurance Broker</span>
          </div>
        </button>

        <nav className="hidden lg:flex items-center gap-7">
          {NAV.map((n) => (
            <button
              key={n.id}
              onClick={() => go(n.id)}
              data-testid={`nav-${n.id}`}
              className="relative text-sm tracking-wide text-ink/70 hover:text-ink transition-colors duration-300 font-medium group"
            >
              {n.label}
              <span className="absolute left-0 -bottom-1 w-0 h-px bg-gold group-hover:w-full transition-all duration-500" />
            </button>
          ))}
        </nav>

        <a
          href={BROKER.phoneLink}
          data-testid="nav-call-cta"
          className="hidden md:inline-flex items-center gap-2 bg-ink text-gold-light border border-ink px-5 py-2.5 text-sm font-medium tracking-wide hover:bg-gold hover:text-ink hover:border-gold transition-colors"
        >
          <Phone className="w-4 h-4" />
          {BROKER.phone}
        </a>

        <button
          className="lg:hidden text-ink"
          onClick={() => setOpen((o) => !o)}
          data-testid="nav-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-ivory/95 backdrop-blur-xl border-t border-line overflow-hidden"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {NAV.map((n) => (
                <button
                  key={n.id}
                  onClick={() => go(n.id)}
                  data-testid={`nav-mobile-${n.id}`}
                  className="text-left text-base text-ink/80 hover:text-gold tracking-wide font-medium"
                >
                  {n.label}
                </button>
              ))}
              <a
                href={BROKER.phoneLink}
                data-testid="nav-mobile-call"
                className="mt-2 inline-flex items-center justify-center gap-2 bg-ink text-gold-light px-5 py-3 text-sm font-medium tracking-wide"
              >
                <Phone className="w-4 h-4" /> {BROKER.phone}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default NavBar;
